import { relayClerkTokenOptions } from "@cleo/shared/relayAuth";
import { normalizeSecureRelayUrl } from "@cleo/shared/relayUrl";
import * as Schema from "effect/Schema";

export class CloudPublicConfigMissingError extends Schema.TaggedErrorClass<CloudPublicConfigMissingError>()(
  "CloudPublicConfigMissingError",
  {
    key: Schema.Literal("CLEO_CLERK_JWT_TEMPLATE"),
  },
) {
  override get message(): string {
    return `${this.key} is not configured.`;
  }
}

export interface CloudPublicConfig {
  readonly clerkPublishableKey: string | null;
  readonly clerkJwtTemplate: string | null;
  readonly relayUrl: string | null;
  readonly relayTracing: {
    readonly tracesUrl: string | null;
    readonly tracesDataset: string | null;
    readonly tracesToken: string | null;
  };
}

export function trimNonEmpty(value: string | undefined): string | null {
  return value?.trim() || null;
}

function normalizeSecureUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

export function resolveCloudPublicConfig(): CloudPublicConfig {
  return {
    clerkPublishableKey: trimNonEmpty(
      import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined,
    ),
    clerkJwtTemplate: trimNonEmpty(import.meta.env.VITE_CLERK_JWT_TEMPLATE as string | undefined),
    relayUrl: normalizeSecureRelayUrl(
      (import.meta.env.VITE_CLEO_RELAY_URL as string | undefined) ?? "",
    ),
    relayTracing: {
      tracesUrl: normalizeSecureUrl(
        (import.meta.env.VITE_RELAY_OTLP_TRACES_URL as string | undefined) ?? "",
      ),
      tracesDataset: trimNonEmpty(
        import.meta.env.VITE_RELAY_OTLP_TRACES_DATASET as string | undefined,
      ),
      tracesToken: trimNonEmpty(import.meta.env.VITE_RELAY_OTLP_TRACES_TOKEN as string | undefined),
    },
  };
}

export function resolveRelayTracingConfig() {
  const { relayTracing } = resolveCloudPublicConfig();
  return relayTracing.tracesUrl && relayTracing.tracesDataset && relayTracing.tracesToken
    ? {
        tracesUrl: relayTracing.tracesUrl,
        tracesDataset: relayTracing.tracesDataset,
        tracesToken: relayTracing.tracesToken,
      }
    : null;
}

export function hasCloudPublicConfig(): boolean {
  const config = resolveCloudPublicConfig();
  return Boolean(config.clerkPublishableKey && config.clerkJwtTemplate && config.relayUrl);
}

/**
 * The frontend-API domain a Clerk publishable key is locked to: the key is
 * `pk_live_`/`pk_test_` plus base64 of `"<frontend-api>$"`. Null when the
 * key does not decode to that shape.
 */
export function clerkFrontendApiFromPublishableKey(publishableKey: string): string | null {
  const encoded = /^pk_(?:live|test)_([A-Za-z0-9+/=_-]+)$/.exec(publishableKey)?.[1];
  if (encoded === undefined) return null;
  const raw = encoded.replaceAll("-", "+").replaceAll("_", "/");
  const padded = raw + "=".repeat((4 - (raw.length % 4)) % 4);
  try {
    const decoded = atob(padded);
    return decoded.endsWith("$") ? decoded.slice(0, -1) : null;
  } catch {
    return null;
  }
}

export type CloudClerkMountDecision =
  | { readonly mount: true }
  | { readonly mount: false; readonly reason: string };

/**
 * §7.6 dev health: a PRODUCTION Clerk instance refuses every request from an
 * origin outside its locked apex, so booting it from a dev origin only buys
 * a refused-request flood and a dead auth surface. Skip the mount ONLY when
 * that doom is provable — a dev serve, a production key, a decodable apex
 * that does not cover the hostname. pk_test keys, production builds, and
 * unparseable keys mount exactly as before.
 */
export function cloudClerkMountDecision(input: {
  readonly publishableKey: string;
  readonly hostname: string;
  readonly dev: boolean;
}): CloudClerkMountDecision {
  if (!input.dev) return { mount: true };
  if (!input.publishableKey.startsWith("pk_live_")) return { mount: true };
  const frontendApi = clerkFrontendApiFromPublishableKey(input.publishableKey);
  if (frontendApi === null) return { mount: true };
  const apex = frontendApi.startsWith("clerk.") ? frontendApi.slice("clerk.".length) : frontendApi;
  if (input.hostname === apex || input.hostname.endsWith(`.${apex}`)) return { mount: true };
  return {
    mount: false,
    reason: `Cleo Connect auth is off for this dev serve: the configured Clerk production key only serves ${apex}, and this origin is ${input.hostname}.`,
  };
}

export interface CloudClerkMountState {
  readonly mount: boolean;
  /** Present only when a CONFIGURED client was deliberately skipped. */
  readonly reason: string | null;
}

/**
 * THE one answer to "is the cloud ClerkProvider mounted": configuration
 * presence AND origin compatibility (DH1). Every component that calls a
 * Clerk hook must gate on this, never on key presence alone — presence
 * stopped implying a mounted provider the moment DH1 made the mount
 * conditional, and a presence-gated useAuth outside the provider throws.
 */
export function resolveCloudClerkMount(): CloudClerkMountState {
  const config = resolveCloudPublicConfig();
  // Clerk mounts with its publishable key and JWT template; the relay URL is optional. The window's own World sign-in
  // (WorldClerkSession) needs no relay, so a Dev app from source (no relay) still signs its window in and only relay
  // features are off (they gate on hasCloudPublicConfig, which still asks all three). Every production build has all three.
  if (!config.clerkPublishableKey || !config.clerkJwtTemplate) {
    return { mount: false, reason: null };
  }
  if (typeof window === "undefined") {
    return { mount: false, reason: null };
  }
  const decision = cloudClerkMountDecision({
    publishableKey: config.clerkPublishableKey,
    hostname: window.location.hostname,
    dev: import.meta.env.DEV,
  });
  return decision.mount ? { mount: true, reason: null } : { mount: false, reason: decision.reason };
}

export function resolveRelayClerkTokenOptions() {
  const { clerkJwtTemplate } = resolveCloudPublicConfig();
  if (!clerkJwtTemplate) {
    throw new CloudPublicConfigMissingError({ key: "CLEO_CLERK_JWT_TEMPLATE" });
  }
  return relayClerkTokenOptions(clerkJwtTemplate);
}
